'use client';

import { useState, useEffect, useCallback } from 'react';
import { collection, query, orderBy, limit, where, getDocs, DocumentData, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';

// TypeScript interfaces based on the Firestore data structure
export interface FirestoreNFTEvent {
  id: string;
  eventType: 'sold' | 'listed';
  collection: string;
  collectionName: string;
  tokenId: string;
  tokenName: string;
  price: string;
  currency: string;
  formattedPrice: string;
  formattedUSDPrice?: string;
  blockchain: string;
  openseaUrl: string;
  buyer?: string;
  seller?: string;
  timestamp: string | Timestamp | any;
  ogTitle: string;
  ogDescription: string;
  ogImage: string; // Individual NFT image from Telegram bot!
  ogUrl: string;
  twitterShareUrl: string;
}

interface UseFirestoreNFTActivityOptions {
  collection?: string;
  limit?: number;
  enablePolling?: boolean;
  pollingInterval?: number; // milliseconds, default 60000 (1 minute)
}

interface UseFirestoreNFTActivityReturn {
  events: FirestoreNFTEvent[];
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  isPolling: boolean;
  refetch: () => Promise<void>;
}

export function useFirestoreNFTActivity(options: UseFirestoreNFTActivityOptions = {}): UseFirestoreNFTActivityReturn {
  const {
    collection: collectionFilter,
    limit: limitCount = 50,
    enablePolling = true,
    pollingInterval = 60000, // 1 minute default
  } = options;

  const [events, setEvents] = useState<FirestoreNFTEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Fetch function that can be called manually or by polling
  const fetchEvents = useCallback(async (): Promise<void> => {
    try {
      setError(null);
      
      // Skip during build time or when Firebase is not available
      if (!db || typeof window === 'undefined') {
        setLoading(false);
        return;
      }
      
      // Build query based on collection filter
      let nftQuery = query(
        collection(db, 'nft_activity'),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );

      // Add collection filter if specified
      if (collectionFilter) {
        nftQuery = query(
          collection(db, 'nft_activity'),
          where('collection', '==', collectionFilter),
          orderBy('timestamp', 'desc'),
          limit(limitCount)
        );
      }

      // Execute query
      const snapshot = await getDocs(nftQuery);
      const nftEvents: FirestoreNFTEvent[] = [];
      
      snapshot.forEach((doc) => {
        const eventData = doc.data() as Omit<FirestoreNFTEvent, 'id'>;
        nftEvents.push({
          id: doc.id,
          ...eventData,
        });
      });

      setEvents(nftEvents);
      setLastUpdated(new Date());
      setLoading(false);
    } catch (err) {
      // Handle errors gracefully during build
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.warn('Failed to fetch NFT activity:', errorMessage);
      setError(`Failed to fetch NFT activity: ${errorMessage}`);
      setLoading(false);
    }
  }, [collectionFilter, limitCount]);

  // Set up polling effect
  useEffect(() => {
    if (!enablePolling) {
      setLoading(false);
      return;
    }

    // Initial fetch
    fetchEvents();

    // Set up polling interval
    const intervalId = setInterval(() => {
      fetchEvents();
    }, pollingInterval);

    // Cleanup on unmount or dependency change
    return () => {
      clearInterval(intervalId);
    };
  }, [fetchEvents, enablePolling, pollingInterval]);

  return {
    events,
    loading,
    error,
    lastUpdated,
    isPolling: enablePolling,
    refetch: fetchEvents,
  };
}

// Helper function to format NFT event for display (maintains compatibility)
export function formatFirestoreNFTEvent(event: FirestoreNFTEvent) {
  // Parse timestamp with better error handling
  let parsedDate: Date;
  
  if (!event.timestamp) {
    parsedDate = new Date(); // Default to now if no timestamp
  } else if (event.timestamp && typeof event.timestamp === 'object' && 'toDate' in event.timestamp) {
    // Firestore Timestamp object
    parsedDate = event.timestamp.toDate();
  } else if (typeof event.timestamp === 'string') {
    // Handle various timestamp formats
    if (event.timestamp.includes('T') || event.timestamp.includes('-')) {
      // ISO string format
      parsedDate = new Date(event.timestamp);
    } else if (/^\d+$/.test(event.timestamp)) {
      // Unix timestamp (seconds or milliseconds)
      const numTimestamp = parseInt(event.timestamp);
      // If less than 13 digits, assume seconds; otherwise milliseconds
      parsedDate = new Date(numTimestamp < 10000000000 ? numTimestamp * 1000 : numTimestamp);
    } else {
      // Try parsing as-is
      parsedDate = new Date(event.timestamp);
    }
  } else if (typeof event.timestamp === 'number') {
    // Unix timestamp as number
    parsedDate = new Date(event.timestamp < 10000000000 ? event.timestamp * 1000 : event.timestamp);
  } else {
    // If timestamp is already a Date or unknown format
    parsedDate = new Date(event.timestamp);
  }

  const timeAgo = getTimeAgo(parsedDate);

  return {
    ...event,
    timeAgo,
    shortAddress: {
      buyer: shortenAddress(event.buyer),
      seller: shortenAddress(event.seller),
    },
  };
}

// Helper function to get time ago string
function getTimeAgo(date: Date): string {
  const now = new Date();
  
  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return 'unknown';
  }
  
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  // Handle future dates or invalid calculations
  if (diffInSeconds < 0 || isNaN(diffInSeconds)) {
    return 'just now';
  }
  
  if (diffInSeconds < 60) {
    return `${diffInSeconds}s ago`;
  }
  
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  }
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  }
  
  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays}d ago`;
}

// Helper function to shorten Ethereum addresses
function shortenAddress(address?: string): string {
  if (!address) return '';
  if (address.length <= 10) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}
